import { reinitializeSwipers } from './swiper-element.js';
import { addToCartFromCard } from './form-product.js';
import { trapFocus, reTrapFocusAfterUpdate, reanimateImages } from './utils.js';

// Quick View Drawer Logic (fetch and populate only)

document.addEventListener('DOMContentLoaded', function () {
  const drawer = document.querySelector('drawer-component[data-drawer="quick-view"]');
  const drawerContent = drawer?.querySelector('.quick-view__product');
  const viewFullDetailsLink = drawer?.querySelector('a.drawer__close');

  if (!drawer) {
    console.error('Quick view drawer component not found.');
    return;
  }

  // Cache for preloaded content
  const preloadCache = new Map();

  const removeDrawerAttributeElements = (root) => {
    if (!root || !(root instanceof Element)) return;
    root.querySelectorAll('[data-drawer]').forEach((el) => el.remove());
  }

  // Preload content on hover
  document.body.addEventListener('mouseenter', function (e) {
    const btn = e.target.closest('button[data-drawer="quick-view"]');
    if (!btn) return;

    const productUrl = btn.getAttribute('data-product');
    if (!productUrl || preloadCache.has(productUrl)) return;

        // Start preloading
    preloadCache.set(productUrl, 'loading');

    const targetPageUrl = new URL(productUrl, window.location.origin);

    fetch(targetPageUrl.toString(), { headers: { 'X-Requested-With': 'XMLHttpRequest' } })
      .then(res => res.ok ? res.text() : Promise.reject(`Network error: ${res.status}`))
      .then(htmlText => {
        preloadCache.set(productUrl, htmlText);
      })
      .catch(err => {
        console.log('Preload failed (not critical):', err);
        preloadCache.delete(productUrl);
      });
  }, true);

  // Listen for Quick View button clicks (event delegation)
  document.body.addEventListener('click', async function (e) {
    const btn = e.target.closest('button[data-drawer="quick-view"]');
    if (!btn) return;

    // Ensure this click is intended for the main quick-view drawer we are handling
    // (This is more relevant if multiple data-drawer="quick-view" instances could exist for different components)
    // For now, we assume any such button targets our single quick-view drawer.

    const productUrl = btn.getAttribute('data-product');
    if (!productUrl || !drawerContent) {
        console.error("Quick view button is missing product URL or drawer content area is not found.");
        return;
    }

    // Call the drawer's open method. This should handle the overlay as per drawer.js logic.
    if (typeof drawer.open === 'function') {
      drawer.open();
    } else {
      console.error("Quick view drawer does not have an open method. Cannot trigger overlay.");
      // Optionally, prevent further execution if opening is critical
      // return;
        }

    // Reset to loading state
    drawerContent.innerHTML = '<div class="quick-view__loading"><svg width="24px" height="16px" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 100 100" preserveAspectRatio="xMidYMid"><circle cx="50" cy="50" r="40" stroke-dasharray="163.36281798666926 87.9645943005142" stroke="currentColor" fill="none" stroke-width="8"><animateTransform attributeName="transform" type="rotate" values="0 50 50;180 50 50;360 50 50;" keyTimes="0;0.4;1" dur="0.8s" repeatCount="indefinite" begin="0s"></animateTransform></circle></svg></div>';

    try {
      let htmlText;

      // Check if content is already cached
      if (preloadCache.has(productUrl) && preloadCache.get(productUrl) !== 'loading') {
        htmlText = preloadCache.get(productUrl);
      } else {
        // Fetch if not cached
        const targetPageUrl = new URL(productUrl, window.location.origin);
        const fetchUrlString = targetPageUrl.toString();

        const res = await fetch(fetchUrlString, { headers: { 'X-Requested-With': 'XMLHttpRequest' } });
        if (!res.ok) throw new Error(`Network error: ${res.status} ${res.statusText}`);
        htmlText = await res.text();

        // Cache the result
        preloadCache.set(productUrl, htmlText);
      }

      const tempParser = document.createElement('div');
      tempParser.innerHTML = htmlText;

      // Extract the main product section from the full page
      const mainProductSection = tempParser.querySelector('.main-product');

      if (mainProductSection) {
        // Clone the content to avoid modifying the original
        const clonedContent = mainProductSection.cloneNode(true);

        // Remove unwanted elements (pdp-lightbox, gallery thumbnails, USPs, drawer components)
        const elementsToRemove = clonedContent.querySelectorAll(
          '.pdp-lightbox, .swiper-thumbnails, .usp-product--availability, .usp-product, drawer-component, .quick-view-hide'
        );
        elementsToRemove.forEach(element => element.remove());

        // Ensure Quick View content cannot include drawer triggers (cart/nav/quick-view/etc).
        // Only remove from the injected product content, not the drawer chrome itself.
        removeDrawerAttributeElements(clonedContent);

        drawerContent.innerHTML = clonedContent.innerHTML;

        // Safety pass in case any nodes were reintroduced via scripts/components.
        removeDrawerAttributeElements(drawerContent);

        if (viewFullDetailsLink) {
          const targetPageUrl = new URL(productUrl, window.location.origin);
          viewFullDetailsLink.setAttribute('href', targetPageUrl.toString());
        }

        const swiperEl = drawerContent.querySelector('swiper-container');
        if (swiperEl) {
          swiperEl.setAttribute('data-desktop-layout', 'slider');
          swiperEl.setAttribute('data-mobile-layout', 'slider');
          swiperEl.setAttribute('data-desktop-slides-per-view', '1');
          swiperEl.setAttribute('data-mobile-slides-per-view', '1');
        }
        reinitializeSwipers();
        addToCartFromCard();

        // Re-animate any newly inserted images and re-trap focus
        reanimateImages(drawerContent)
        reTrapFocusAfterUpdate({
          drawer,
          root: drawer,
          selector: 'button, input, select, textarea, a[href], [tabindex]:not([tabindex^="-"])',
          delay: 500,
        })

      } else {
        console.error('Section with class .main-product not found on page:', productUrl);
        drawerContent.innerHTML = '<div class="quick-view__error">Failed to load product content. Please try again.</div>';
      }
    } catch (err) {
      console.error('Quick view fetch error:', err);
      drawerContent.innerHTML = '<div class="quick-view__error">Failed to load product. Please try again.</div>';
    }
  });
});
